import { Gender, IdentityDocumentType } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { isoDateString } from '../_shared/dates.js';
import { withRenamedDeprecatedKey } from '../_shared/deprecated-key.js';
import { personName } from '../_shared/person-name.js';
import { bookingWithoutPnrSchema } from './booking-without-pnr.js';
/**
 * IGA pvt accounts selectable when importing/creating a booking from Affidium.
 * WEB (site-web) is reserved for autonomous checkout bookings and is never
 * chosen here. Defaults to the booking's agency first (RUN -> WSMASSILIA2),
 * with Marseille bookings still using WSMASSILIA2 for RUN departures.
 * WSMASSILIA4 (MASSILIA B2B) is auto-picked when the client is a COMPANY.
 */
export const igaPvtAccountSchema = z.enum(['WSMASSILIA1', 'WSMASSILIA2', 'WSMASSILIA4']);
/** All IGA accounts, including WEB (autonomous checkout). */
export const igaAccountSchema = z.enum(['WEB', 'WSMASSILIA1', 'WSMASSILIA2', 'WSMASSILIA4']);
/**
 * Human labels for the IGA portefeuilles. Single source of truth: le formulaire
 * de création (choix du compte) et la fiche dossier (compte de synchronisation)
 * doivent nommer un portefeuille de la même façon.
 */
export const IGA_ACCOUNT_LABELS = {
    WEB: 'Site web',
    WSMASSILIA1: 'Marseille',
    WSMASSILIA2: 'Réunion',
    WSMASSILIA4: 'Massilia B2B',
};
export function resolveAffidiumImportIgaAccount({ agency, clientType, departureLocation, }) {
    if (clientType === 'COMPANY')
        return 'WSMASSILIA4';
    if (agency === 'RUN' || departureLocation === 'RUN')
        return 'WSMASSILIA2';
    return 'WSMASSILIA1';
}
/**
 * Resolve a client's IGA customer code for a given IGA account. Codes are
 * per-account (each account is a distinct portefeuille), so the value shown for a
 * booking must match that booking's `igaAccountId`. Shared by the server (invoice
 * PDF) and the backoffice (booking detail) to keep resolution consistent.
 */
export function resolveClientIgaCode(igaCodes, account) {
    return igaCodes.find((c) => c.account === account)?.code ?? null;
}
/**
 * Un voyageur saisi au formulaire d'import, rattaché à UN PNR par le `travellerId`
 * (xRef) qu'il y porte. Même forme pour tous les PNR du dossier : l'écran présente une
 * carte par personne, sans voyage privilégié, et l'identité saisie part avec chaque
 * voyage où la personne figure.
 */
const affidiumPassengerPatchSchema = z.object({
    travellerId: z.string().min(1),
    /** Facultatif sur un dossier neutre seulement : cf. le `superRefine` plus bas. */
    birthDate: isoDateString().optional(),
    gender: z.nativeEnum(Gender),
    identityDocumentExpiryDate: isoDateString().nullish(),
    identityDocumentNumber: z.string().nullish(),
    identityDocumentType: z.nativeEnum(IdentityDocumentType).nullish(),
    issuingCountry: z.string().length(3).nullish(),
    /** Facultatif sur un dossier neutre seulement ; à défaut le serveur pose `FRA`. */
    nationalityCode: z.string().length(3).optional(),
    price: z.number().optional(),
});
/**
 * Un PNR supplémentaire importé dans le même geste que le dossier.
 *
 * Chaque ligne porte SA référence et SON account code (paramètre de recherche, qui peut
 * différer d'un PNR à l'autre), SES conditions d'échange et de remboursement — déduites
 * une fois par PNR, la compagnie et la destination n'étant pas les mêmes — et SES
 * voyageurs. Le premier PNR est décrit à plat ci-dessous : c'est celui qui porte la
 * référence du dossier, pas un voyage privilégié.
 */
const additionalAffidiumTripSchema = z.object({
    accountCode: z.string().optional(),
    bookingReference: z.string().min(1),
    exchangeAllowed: z.boolean(),
    exchangeFeeDescription: z.string(),
    /** Voyageurs de CE PNR : identité saisie et prix de vente, par `travellerId` (xRef). */
    passengers: z.array(affidiumPassengerPatchSchema).optional(),
    refundAllowed: z.boolean(),
    refundFeeDescription: z.string(),
});
// `installmentPaymentDeadlineOverride` est l'ancien nom de `paymentDeadlineOverride` :
// alias déprécié, à supprimer à la release suivante.
export const createBookingFromAffidiumSchema = withRenamedDeprecatedKey(z
    .object({
    clientId: z.string().optional(),
    igaAccountId: igaPvtAccountSchema.optional(),
    reservationId: z.string().optional(),
    accountCode: z.string().optional(),
    /** Absent ou vide = import mono-PNR, inchangé. */
    additionalTrips: z.array(additionalAffidiumTripSchema).optional(),
    // ≤ 50 : la référence finit en `supplierFolder` IGA, qui répond 500 au-delà.
    bookingReference: z.string().min(1).max(50),
    depositAmountOverride: z.number().optional(),
    depositDeadlineOverride: isoDateString().optional(),
    exchangeAllowed: z.boolean(),
    exchangeFeeDescription: z.string(),
    igaReference: z.string().optional(),
    installmentChosen: z.boolean().optional(),
    passengers: z.array(affidiumPassengerPatchSchema).optional(),
    paymentDeadlineOverride: isoDateString().optional(),
    refundAllowed: z.boolean(),
    refundFeeDescription: z.string(),
    sendReservationContract: z.boolean().optional(),
    /** SMS en plus du mail de contrat. Absent = pas de SMS. */
    sendReservationSms: z.boolean().optional(),
    /**
     * Dossier neutre : la vente a été conclue à l'agence, on l'inscrit pour
     * les statistiques et le mailing, et rien d'autre ne s'y applique. Réservé à
     * l'import par PNR — un dossier né d'un devis a traversé tout le cycle de
     * vente, acompte compris, la case n'y aurait aucun sens.
     */
    untracked: z.boolean().optional(),
    /** Présent = dossier sans PNR : `bookingReference` est la référence du fournisseur. */
    withoutPnr: bookingWithoutPnrSchema.optional(),
})
    .superRefine((payload, ctx) => {
    if (payload.withoutPnr && payload.additionalTrips?.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Un dossier sans PNR ne porte pas de PNR supplémentaire',
            path: ['additionalTrips'],
        });
    }
    // Un dossier neutre ne sert qu'aux statistiques et au mailing : l'agent
    // au comptoir n'a pas toujours la date de naissance ni la nationalité, et
    // rien en aval (IGA, assurance, espace client) ne les lira.
    if (payload.untracked)
        return;
    const lists = [
        [['passengers'], payload.passengers ?? []],
        ...(payload.additionalTrips ?? []).map((trip, tripIndex) => [
            ['additionalTrips', tripIndex, 'passengers'],
            trip.passengers ?? [],
        ]),
    ];
    for (const [path, passengers] of lists) {
        for (const [index, passenger] of passengers.entries()) {
            if (!passenger.birthDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Date de naissance obligatoire',
                    path: [...path, index, 'birthDate'],
                });
            }
            if (!passenger.nationalityCode) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Nationalité obligatoire',
                    path: [...path, index, 'nationalityCode'],
                });
            }
        }
    }
}), 'installmentPaymentDeadlineOverride', 'paymentDeadlineOverride');
export const reconcileBookingPassengerItemSchema = z.object({
    bookingPassengerId: z.string().min(1),
    firstName: personName({ optional: true }),
    lastName: personName({ optional: true }),
    targetPassengerId: z.string().min(1).optional(),
    action: z.enum(['CREATE', 'LINK', 'SKIP']),
    birthDate: isoDateString().optional(),
    gender: z.nativeEnum(Gender).optional(),
    nationalityCode: z.string().min(2).optional(),
});
export const reconcileBookingPassengersSchema = z.object({
    dryRun: z.boolean(),
    items: z.array(reconcileBookingPassengerItemSchema).optional(),
});
export const updateBookingPassengerSchema = z.object({
    firstName: personName({ optional: true }),
    lastName: personName({ optional: true }),
    birthDate: isoDateString().nullish(),
    canceled: z.boolean().optional(),
    gender: z.nativeEnum(Gender).optional(),
    identityDocumentExpiryDate: isoDateString().nullish(),
    identityDocumentNumber: z.string().nullish(),
    identityDocumentType: z.nativeEnum(IdentityDocumentType).nullish(),
    issuingCountry: z.string().length(3).nullish(),
    nationalityCode: z.string().length(3).optional(),
    price: z.number().int().min(0).optional(),
});
/**
 * Ce qu'un passager porte SUR un voyage : prix de vente en centimes et numéros de billet
 * (dossier multi-PNR). Les champs du dossier, eux, n'en sont que la somme et l'union :
 * ils ne se saisissent plus. Les deux champs sont optionnels — l'écran envoie ce que
 * l'agent a modifié.
 */
export const updateTripPassengerSchema = z.object({
    price: z.number().int().min(0).optional(),
    ticketNumbers: z.array(z.string().trim().min(1)).optional(),
});
